import { z } from 'zod';

/**
 * Feature codes: what an account's phones dial to change something themselves.
 *
 * Every code is the account's to choose (docs/26 E1). One moving from another
 * phone system keeps the codes its people already dial rather than retraining
 * all of them, and the defaults are the widely recognised ones -- somebody who
 * has used a phone system before already knows *78.
 *
 * Rows are stored only where an account differs from the default (API
 * telephony/featurecodes.ts), and the switch lays them over the same defaults
 * (agent fsxml.ts, resolveFeatureCodes).
 */
export const FEATURE_CODE_ACTIONS = [
  'dnd_on', 'dnd_off', 'fwd_on', 'fwd_off', 'hotdesk_login', 'hotdesk_logout',
  'voicemail_own', 'voicemail_other', 'queue_pause', 'queue_unpause',
] as const;
export type FeatureCodeAction = (typeof FEATURE_CODE_ACTIONS)[number];

export interface FeatureCodeInfo {
  action: FeatureCodeAction;
  group: string;
  label: string;
  /** What is dialled after the code, for the codes that take something. */
  then: string | null;
}

/** In the order a person reads them, grouped by what they are for. */
export const FEATURE_CODE_CATALOGUE: readonly FeatureCodeInfo[] = [
  { action: 'dnd_on', group: 'Do not disturb', label: 'Turn on', then: null },
  { action: 'dnd_off', group: 'Do not disturb', label: 'Turn off', then: null },
  { action: 'fwd_on', group: 'Forwarding', label: 'Forward every call', then: 'the extension to forward to' },
  { action: 'fwd_off', group: 'Forwarding', label: 'Stop forwarding', then: null },
  { action: 'hotdesk_login', group: 'Hot desking', label: 'Sit down at a shared desk', then: 'extension, *, PIN' },
  { action: 'hotdesk_logout', group: 'Hot desking', label: 'Leave the desk', then: null },
  { action: 'voicemail_own', group: 'Voicemail', label: 'Own mailbox', then: null },
  { action: 'voicemail_other', group: 'Voicemail', label: 'Any mailbox, asked for', then: null },
  { action: 'queue_pause', group: 'Queues', label: 'Pause in every queue', then: null },
  { action: 'queue_unpause', group: 'Queues', label: 'Take calls again', then: null },
];

/**
 * What an account dials until somebody chooses otherwise. Mirrored in
 * apps/agent/src/fsxml.ts, for a node whose control plane has sent no codes.
 *
 * The unpause default matters most: mod_callcenter pauses an agent by itself
 * after three unanswered queue calls in a row, and an account with no unpause
 * code would leave that agent no way back.
 */
export const DEFAULT_FEATURE_CODES: Readonly<Record<FeatureCodeAction, string>> = {
  dnd_on: '*78', dnd_off: '*79',
  fwd_on: '*72', fwd_off: '*73',
  hotdesk_login: '*31', hotdesk_logout: '*32',
  voicemail_own: '*97', voicemail_other: '*98',
  queue_pause: '*46', queue_unpause: '*47',
};

/** Codes followed by digits: *721002 forwards to 1002, *311001*4729 sits down. */
export const TAKES_DIGITS: ReadonlySet<FeatureCodeAction> = new Set<FeatureCodeAction>(['fwd_on', 'hotdesk_login']);

/** A star and one to eight digits: dialable on any handset, never an extension. */
export const FEATURE_CODE_PATTERN = /^\*[0-9]{1,8}$/;

/** Why a single code is not usable as written, or null. */
export function featureCodeProblem(code: string): string | null {
  return FEATURE_CODE_PATTERN.test(code)
    ? null
    : 'Use a star followed by one to eight digits, for example *46.';
}

const labelOf = (action: FeatureCodeAction): string => {
  const info = FEATURE_CODE_CATALOGUE.find((c) => c.action === action)!;
  return `${info.group}, ${info.label.toLowerCase()}`;
};

/**
 * Why a whole set does not work together, per action.
 *
 * Two rules. No two actions share a code. And nothing may START with a code
 * that takes digits after it, because the switch reads whatever follows as
 * those digits: with forwarding on *72, a pause code of *7212 is "forward my
 * calls to 12". A code that is merely a prefix OF another is fine -- the switch
 * matches whole strings.
 */
export function featureCodeClashes(
  codes: Readonly<Record<FeatureCodeAction, string>>,
): Partial<Record<FeatureCodeAction, string>> {
  const out: Partial<Record<FeatureCodeAction, string>> = {};
  for (const a of FEATURE_CODE_ACTIONS) {
    const problem = featureCodeProblem(codes[a]);
    if (problem) { out[a] = problem; continue; }
    for (const b of FEATURE_CODE_ACTIONS) {
      if (a === b || featureCodeProblem(codes[b])) continue;
      if (codes[a] === codes[b]) {
        out[a] = `${codes[a]} is also ${labelOf(b)}.`;
        break;
      }
      if (TAKES_DIGITS.has(b) && codes[a].startsWith(codes[b])) {
        out[a] = `Starts with ${codes[b]} (${labelOf(b)}), which reads what follows as digits.`;
        break;
      }
    }
  }
  return out;
}

/**
 * The whole set, every time. Saving the codes together is what makes the clash
 * rules checkable: a change to one can only clash with the others as they will
 * be, not as they were.
 */
export const FeatureCodesUpdateSchema = z.object({
  dnd_on: z.string().trim(),
  dnd_off: z.string().trim(),
  fwd_on: z.string().trim(),
  fwd_off: z.string().trim(),
  hotdesk_login: z.string().trim(),
  hotdesk_logout: z.string().trim(),
  voicemail_own: z.string().trim(),
  voicemail_other: z.string().trim(),
  queue_pause: z.string().trim(),
  queue_unpause: z.string().trim(),
}).strict().superRefine((v, issue) => {
  const clashes = featureCodeClashes(v);
  for (const action of FEATURE_CODE_ACTIONS) {
    const message = clashes[action];
    if (message) issue.addIssue({ code: z.ZodIssueCode.custom, path: [action], message });
  }
});
export type FeatureCodesUpdate = z.infer<typeof FeatureCodesUpdateSchema>;

/** What an account dials now, beside what it would dial with no choices made. */
export interface FeatureCodes {
  codes: Record<FeatureCodeAction, string>;
  defaults: Readonly<Record<FeatureCodeAction, string>>;
}
